import { Suspense, forwardRef, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { FormikProps } from "formik"

import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { QueryErrorBoundary } from "../../../../../../core/api/components/QueryErrorBoundary"
import { LoadingSpinner } from "../../../../../../shared/components/loading/LoadingSpinner"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { SuccessDisplay } from "../../../../../../core/api/errorTypes"
import { COMPANY_ADMIN_ORGANIZATION_WORKGROUPS_ROUTE, COMPANY_ADMIN_ROUTE } from "../../../../../../constants/routes"
import { useCurrentCompany } from "../../../../../company/hooks/useCurrentCompany"

import { CompanyAdminWorkgroupsQuery, createWorkgroupForCompany } from "../workgroupsApi"
import { CreateUpdateWorkgroup } from "../workgroupsTypes"
import { CreateUpdateWorkgroupForm } from "../components/CreateUpdateWorkgroupForm"

const Workgroup = forwardRef<FormikProps<CreateUpdateWorkgroup>>((props, ref) => {
    const { companyId } = useCurrentCompany()
    const navigate = useNavigate()

    const { mutate: callCreateWorkgroup } = useApiMutation(createWorkgroupForCompany, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(_params, _data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [CompanyAdminWorkgroupsQuery.LIST] })
            navigate(COMPANY_ADMIN_ORGANIZATION_WORKGROUPS_ROUTE)
        },
    })

    return (
        <CreateUpdateWorkgroupForm
            ref={ref}
            onSubmit={(data) => {
                callCreateWorkgroup({ workgroup: data, companyId })
            }}
        />
    )
})

export function CreateWorkgroup() {
    const workgroupFormRef = useRef<FormikProps<CreateUpdateWorkgroup>>(null)

    const handleSaveClick = () => {
        workgroupFormRef.current?.submitForm()
    }

    return (
        <>
            <LocalizedStrict id="create-workgroup-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.COMPANY_ADMIN_ORGANIZATION_WORKGROUPS_CREATE}
                    title="Create workgroup"
                    rightElement={
                        <LocalizedStrict id="create-workgroup-save-button">
                            <StandardButton type="submit" onClick={handleSaveClick}>
                                Create
                            </StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="company-nav-item-home">
                            <BreadcrumbLink to={COMPANY_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-organization-title">
                            <Breadcrumb>Organization</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-organization-workgroups-title">
                            <BreadcrumbLink to={COMPANY_ADMIN_ORGANIZATION_WORKGROUPS_ROUTE}>Workgroups</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-organization-workgroups-create-title">
                            <Breadcrumb>Create workgroup</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>
            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <Workgroup ref={workgroupFormRef} />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}
